import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api, type UserProfile } from '../api/client';
import { authEnabled } from './config';

interface UserDirectoryValue {
  /** OAuth subject ID for the signed-in user, or null when auth is disabled. */
  selfId: string | null;
  /** Look up a display name (first name) by ID; falls back to `fallback`. */
  displayName: (id: string | undefined | null, fallback?: string) => string;
  /** Force a re-fetch of the directory (e.g. after seeing an unknown ID). */
  refresh: () => Promise<void>;
}

const UserDirectoryContext = createContext<UserDirectoryValue | null>(null);

export function useUserDirectory(): UserDirectoryValue {
  const ctx = useContext(UserDirectoryContext);
  if (!ctx) throw new Error('useUserDirectory must be used within UserDirectoryProvider');
  return ctx;
}

/** Provides a backend-backed user directory plus self-registration on first load. */
export function UserDirectoryProvider({ children }: { children: ReactNode }) {
  if (!authEnabled) return <DisabledProvider>{children}</DisabledProvider>;
  return <Auth0DirectoryProvider>{children}</Auth0DirectoryProvider>;
}

function DisabledProvider({ children }: { children: ReactNode }) {
  const value = useMemo<UserDirectoryValue>(
    () => ({
      selfId: null,
      displayName: (_id, fallback) => fallback ?? 'another editor',
      refresh: async () => {},
    }),
    [],
  );
  return (
    <UserDirectoryContext.Provider value={value}>{children}</UserDirectoryContext.Provider>
  );
}

function pickFirstName(user: ReturnType<typeof useAuth0>['user']): string {
  const given = (user as { given_name?: string } | undefined)?.given_name;
  if (given) return given.trim();
  if (user?.name) return user.name.trim().split(/\s+/)[0] ?? '';
  if (user?.nickname) return user.nickname;
  if (user?.email) return user.email.split('@')[0] ?? user.email;
  return '';
}

function Auth0DirectoryProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth0();
  const [directory, setDirectory] = useState<Map<string, UserProfile>>(new Map());
  const registeredRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listUsers();
      setDirectory(new Map(list.map((p) => [p.id, p])));
    } catch {
      // Non-fatal; lookups fall back to "another editor".
    }
  }, []);

  // Register self with the backend once, then load the directory.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const first = pickFirstName(user);
    if (!first || registeredRef.current) {
      void refresh();
      return;
    }
    registeredRef.current = true;
    void (async () => {
      try {
        await api.updateMe(first);
      } catch {
        // If self-registration fails, keep going — the directory may still
        // contain other users we can name.
      }
      await refresh();
    })();
  }, [isAuthenticated, user, refresh]);

  const value = useMemo<UserDirectoryValue>(
    () => ({
      selfId: user?.sub ?? null,
      displayName: (id, fallback) => {
        if (!id) return fallback ?? 'another editor';
        const p = directory.get(id);
        if (p?.firstName) return p.firstName;
        return fallback ?? 'another editor';
      },
      refresh,
    }),
    [directory, refresh, user?.sub],
  );

  return (
    <UserDirectoryContext.Provider value={value}>{children}</UserDirectoryContext.Provider>
  );
}
